import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import isPast from "date-fns/isPast";
import format from "date-fns/format";
import Box from "../../Unknown/Box";
import Grid from "../../Unknown/Grid";
import Typography from "../../Unknown/Typography";
import { ConfirmedAppointment } from "../AppointmentConfirmedScreen/parseConfirmedAppointment";
import SuggestedPackageList from "./SuggestedPackageList";
import SectionBorder from "./SectionBorder";
import AppointmentUpsellTimer from "./AppointmentUpsellTimer";
import useTranslations from "./useTranslations";
import useUpsellTimer from "./useUpsellTimer";
import SuggestedAddOnList from "./SuggestedAddOnList";
import useSuggestedPackages from "./useSuggestedPackages";
import useSuggestedAddOns, {
  AddOnSelectionHandlerProps,
} from "./useSuggestedAddOns";
import UpsellSummary from "./UpsellSummary";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";
import useBookingPackages from "../AppointmentBookingContext/useBookingPackages";
import useStatAvailability from "../AppointmentBookingContext/useStatAvailability";
import { useAuthContext } from "../../Auth/AuthContextProvider";

type AppointmentUpsellSectionProps = {
  appointment: ConfirmedAppointment;
  refetch: () => Promise<void>;
};

const AppointmentUpsellSection: FC<AppointmentUpsellSectionProps> = ({
  appointment,
  refetch,
}) => {
  const [isConfirmationLoading, setIsConfirmationLoading] = useState(false);
  const [selectedOfferingIds, setSelectedOfferingIds] = useState<
    Record<string, string[]>
  >({});
  const [selectedPackageIds, setSelectedPackageIds] = useState<
    Record<string, string[]>
  >({});
  const [error, setError] = useState<Record<string, string | null>>({});

  const { isCenterUser } = useAuthContext();

  const apptDateStr = format(appointment.timeSlot.startedAt, "yyyy-MM-dd");
  const apptMinutes =
    appointment.timeSlot.startedAt.getHours() * 60 +
    appointment.timeSlot.startedAt.getMinutes();

  const timerProps = useUpsellTimer({ appointmentData: appointment });

  const confirmAppointmentUpsell = useFirebaseAppFunction(
    "confirmAppointmentUpsell",
  );

  const { data: centerPackages } = useBookingPackages({
    isCenterUser,
    centerConfig: appointment.center,
  });

  const { translations } = useTranslations();
  const { sectionTitle } = translations;

  // Must be computed before useStatAvailability so selected upgrade specialties
  // are included in the STAT availability check.
  const suggestedPackagesData = useSuggestedPackages({
    appointmentData: appointment,
    centerPackages,
  });

  const baseRequiredSpecialties = useMemo(() => {
    const specialties = new Set<string>();
    appointment.offerings.forEach((offering) => {
      if (offering.radiologySpecialty)
        specialties.add(offering.radiologySpecialty);
    });
    appointment.packages.forEach((pkg) => {
      pkg.offerings.forEach((offering) => {
        if (offering.radiologySpecialty)
          specialties.add(offering.radiologySpecialty);
      });
    });
    return Array.from(specialties);
  }, [appointment.offerings, appointment.packages]);

  // Include specialties from any package upgrade the user has selected so that
  // STAT availability is re-evaluated against the full set of study types.
  const requiredSpecialties = useMemo(() => {
    const specialties = new Set<string>(baseRequiredSpecialties);
    suggestedPackagesData?.suggestedPackages.forEach((pkg) => {
      if (pkg.isSelected) {
        pkg.centerPackage.radiologySpecialties.forEach((s) =>
          specialties.add(s),
        );
      }
    });
    return Array.from(specialties);
  }, [baseRequiredSpecialties, suggestedPackagesData?.suggestedPackages]);

  const statDateRange = useMemo(
    () => ({ start: apptDateStr, end: apptDateStr }),
    [apptDateStr],
  );

  const statAvailability = useStatAvailability({
    centerId: appointment.center.id,
    requiredSpecialties,
    dateRange: statDateRange,
    skip: isCenterUser,
  });

  const isStatAvailableForAppointment = statAvailability.isAvailable(
    apptDateStr,
    apptMinutes,
  );

  const totalStudiesCount = useMemo(() => {
    const addedStudiesCount = suggestedPackagesData?.suggestedPackages.reduce(
      (count, suggestedPackage) => {
        if (!suggestedPackage.isSelected) {
          return count;
        }

        const addedStudies =
          suggestedPackage.centerPackage.globalStudyIds.length -
          suggestedPackage.forOfferings.length;

        return count + addedStudies;
      },
      0,
    );

    const singleOfferingsCount = appointment.offerings.length;
    const packageOfferingsCount = appointment.packages.reduce(
      (count, appointmentPackage) => {
        return count + appointmentPackage.offerings.length;
      },
      0,
    );

    return (
      singleOfferingsCount + packageOfferingsCount + (addedStudiesCount || 0)
    );
  }, [
    appointment.offerings.length,
    appointment.packages,
    suggestedPackagesData?.suggestedPackages,
  ]);

  const suggestedAddOnsData = useSuggestedAddOns({
    appointmentData: appointment,
    centerPackages,
    studiesCount: totalStudiesCount,
    isStatAvailableForAppointmentTime: isStatAvailableForAppointment,
    isCenterUser,
  });

  const summaryRef = useRef<HTMLDivElement | null>(null);
  const [shouldScrollToSummary, setShouldScrollToSummary] = useState(false);

  const isSummaryShown = useMemo(() => {
    const isPackageUpgraded = suggestedPackagesData?.suggestedPackages.some(
      (pkg) => pkg.isSelected,
    );

    const isAddOnSelected = suggestedAddOnsData?.suggestedAddOns.some(
      (addOn) => addOn.isSelected,
    );

    return isPackageUpgraded || isAddOnSelected;
  }, [suggestedPackagesData, suggestedAddOnsData]);

  const addedPackages = useMemo(() => {
    return suggestedPackagesData?.suggestedPackages.filter(
      (pkg) => pkg.isSelected,
    );
  }, [suggestedPackagesData?.suggestedPackages]);

  const scrollToSummary = useCallback(() => {
    if (summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  useEffect(() => {
    if (isSummaryShown && shouldScrollToSummary) {
      const id = window.requestAnimationFrame(scrollToSummary);
      setShouldScrollToSummary(false);
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [isSummaryShown, shouldScrollToSummary, scrollToSummary]);

  const newTotalPrice = useMemo(() => {
    const packagePriceDiff =
      suggestedPackagesData?.suggestedPackages.reduce(
        (acc, pkg) => acc + (pkg.isSelected ? pkg.priceDiff : 0),
        0,
      ) || 0;

    const selectedAddOnsPriceDiff =
      suggestedAddOnsData?.suggestedAddOns.reduce(
        (acc, addOn) => acc + addOn.appliedPrice,
        0,
      ) || 0;

    return appointment.totalPrice + packagePriceDiff + selectedAddOnsPriceDiff;
  }, [appointment.totalPrice, suggestedPackagesData, suggestedAddOnsData]);

  const onSelectAddOn = useCallback(
    (props: AddOnSelectionHandlerProps) => {
      if (isSummaryShown) {
        scrollToSummary();
      } else {
        setShouldScrollToSummary(true);
      }
      suggestedAddOnsData?.onSelect(props);
    },
    [isSummaryShown, scrollToSummary, suggestedAddOnsData],
  );

  const onResetAddOns = useCallback(() => {
    suggestedAddOnsData?.onReset();
    setSelectedOfferingIds({});
    setSelectedPackageIds({});
    setError({});
  }, [suggestedAddOnsData]);

  const onSelectPackage = useCallback(
    (packageId: string) => {
      if (isSummaryShown) {
        scrollToSummary();
      } else {
        setShouldScrollToSummary(true);
      }
      onResetAddOns();
      suggestedPackagesData?.onSelect(packageId);
    },
    [isSummaryShown, onResetAddOns, scrollToSummary, suggestedPackagesData],
  );

  const onDeselectPackage = useCallback(
    (packageId: string) => {
      onResetAddOns();
      suggestedPackagesData?.onDeselect(packageId);
    },
    [onResetAddOns, suggestedPackagesData],
  );

  const onConfirm = useCallback(async () => {
    setIsConfirmationLoading(true);
    const selectedPackages = suggestedPackagesData?.suggestedPackages
      .filter((pkg) => pkg.isSelected)
      .map((pkg) => ({
        id: pkg.centerPackage.id,
        offeringIds: pkg.forOfferings,
      }));

    const selectedAddOns = suggestedAddOnsData?.suggestedAddOns
      .filter((addOn) => addOn.isSelected)
      .map((addOn) => {
        const addedOfferings = addOn.selectedOfferings.filter(
          (offering) => offering.isAdded,
        );

        return {
          id: addOn.centerAddOn.id,
          packageIds: addOn.selectedPackages
            .filter((pkg) => pkg.isAdded)
            .map((pkg) => pkg.id),
          offeringIds: addedOfferings.map((offering) => offering.id),
          appointmentOfferingIds: addedOfferings.map(
            (offering) => offering.appointmentOfferingId,
          ),
          appliesTo: addOn.centerAddOn.appliesTo,
          quantity: addOn.quantity,
        };
      });

    try {
      await confirmAppointmentUpsell({
        appointmentId: appointment.id,
        packages: selectedPackages || [],
        addOns: selectedAddOns || [],
      });
      await refetch();
    } catch (mutationError) {
      // eslint-disable-next-line no-console
      console.error(mutationError);
    } finally {
      setIsConfirmationLoading(false);
    }
  }, [
    appointment.id,
    confirmAppointmentUpsell,
    refetch,
    suggestedAddOnsData?.suggestedAddOns,
    suggestedPackagesData?.suggestedPackages,
  ]);

  const isEmpty = useMemo(() => {
    return (
      !suggestedPackagesData?.suggestedPackages.length &&
      !suggestedAddOnsData?.suggestedAddOns.length
    );
  }, [suggestedPackagesData, suggestedAddOnsData]);

  const isSectionShown = useMemo(() => {
    return (
      !isEmpty &&
      timerProps?.expiresAt &&
      !isPast(timerProps?.expiresAt) &&
      !appointment.upsellConfirmedAt &&
      appointment.status !== "incomplete"
    );
  }, [isEmpty, timerProps, appointment.upsellConfirmedAt, appointment.status]);

  if (!isSectionShown) {
    return null;
  }

  return (
    <Box p={6} position="relative">
      <SectionBorder />
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Typography variant="h2" align="center">
            {sectionTitle}
          </Typography>
        </Grid>
        {!!timerProps?.expiresAt && (
          <Grid item xs={12}>
            <AppointmentUpsellTimer
              expiresAt={timerProps.expiresAt}
              durationInSeconds={timerProps.durationInSeconds}
            />
          </Grid>
        )}
        {!!suggestedPackagesData?.suggestedPackages.length && (
          <SuggestedPackageList
            appointmentData={appointment}
            suggestedPackages={suggestedPackagesData.suggestedPackages}
            onSelect={onSelectPackage}
            onDeselect={onDeselectPackage}
          />
        )}
        {!!suggestedAddOnsData?.suggestedAddOns.length && (
          <SuggestedAddOnList
            appointmentData={appointment}
            suggestedAddOns={suggestedAddOnsData.suggestedAddOns}
            onSelect={onSelectAddOn}
            addedPackages={addedPackages}
            centerPackages={centerPackages || []}
            onDeselect={suggestedAddOnsData.onDeselect}
            onQuantityChange={suggestedAddOnsData.onQuantityChange}
            selectedOfferingIds={selectedOfferingIds}
            selectedPackageIds={selectedPackageIds}
            error={error}
            setSelectedOfferingIds={setSelectedOfferingIds}
            setSelectedPackageIds={setSelectedPackageIds}
            setError={setError}
          />
        )}
        {/* Anchor for smooth scroll to the summary section */}
        <div ref={summaryRef} />
        {isSummaryShown && (
          <UpsellSummary
            appointmentData={appointment}
            isConfirmationLoading={isConfirmationLoading}
            summaryPrice={newTotalPrice}
            onConfirm={onConfirm}
          />
        )}
      </Grid>
    </Box>
  );
};

export default AppointmentUpsellSection;
